import { EmployeeExtended, EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import { CellContext } from '@tanstack/react-table';
import { Chip, Loading, RoundedPaper } from '../../../atomic';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../../atomic/dataDisplay/drawer';
import { useState } from 'react';
import { useGetEmployee } from '../../../../../queries/employee';
import { useNavigate } from 'react-router';
import { Box, IconButton, Typography } from '@mui/material';
import { LinkShearIcon } from '../../../../icons';
import { EmployeeRoutes, employeeRoutes } from '../../../../../misc/globals';

function HasMissingFieldsCell(info: CellContext<EmployeeListItem, string>) {
  const hasMissingFields = info.getValue();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <>
      <Chip
        label={t(`employee:${hasMissingFields ? 'INCOMPLETE' : 'COMPLETE'}`)}
        color={hasMissingFields ? 'warning' : 'success'}
        size="small"
        onClick={
          hasMissingFields
            ? () => {
                setOpen(true);
              }
            : undefined
        }
      />
      {hasMissingFields ? (
        <HasMissingFieldsDrawer info={info} open={open} setOpen={setOpen} />
      ) : null}
    </>
  );
}

function HasMissingFieldsDrawer({
  info,
  open,
  setOpen
}: Readonly<{
  info: CellContext<EmployeeListItem, string>;
  open: boolean;
  setOpen: (open: boolean) => void;
}>) {
  const { t } = useTranslation();
  const { data: employeeData, isLoading } = useGetEmployee(info.row.original.id ?? '', {
    enabled: open
  });
  return (
    <Drawer label={t('common:MISSING_FIELDS')} open={open} closeDrawer={() => setOpen(false)}>
      {isLoading ? <Loading /> : <MissingDataList employee={employeeData} />}
    </Drawer>
  );
}
function MissingDataList({ employee }: Readonly<{ employee?: EmployeeExtended }>) {
  const { t } = useTranslation('employee_props');
  const navigate = useNavigate();
  const missingFields = [];
  const sections: (keyof EmployeeExtended)[] = [
    'general_data',
    'address',
    'contact_data',
    'tax_data',
    'position_data'
  ];

  interface WithMissingFields {
    missing_fields: string[];
  }

  const hasMissingFields = (
    section: EmployeeExtended[keyof EmployeeExtended] | null | undefined
  ): section is WithMissingFields => {
    return (
      section !== null &&
      section !== undefined &&
      typeof section === 'object' &&
      'missing_fields' in section &&
      Array.isArray((section as WithMissingFields).missing_fields)
    );
  };

  for (const section of sections) {
    const sectionData = employee?.[section];
    if (hasMissingFields(sectionData)) {
      missingFields.push({
        section,
        fields: sectionData.missing_fields
      });
    }
  }

  if (!employee?.user) {
    missingFields.push({
      section: 'user',
      fields: ['email']
    });
  }

  return (
    <>
      {missingFields
        ?.filter((missing) => missing.fields?.length > 0)
        .map((missing) => {
          return (
            <RoundedPaper
              sx={{
                backgroundColor: 'grey.300',
                marginBottom: '1rem',
                padding: '1rem',
                ul: {
                  listStyleType: 'none',
                  margin: '0',
                  padding: '0'
                },
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              }}
              key={missing.section}
            >
              <Box display="flex">
                <Typography flex={1} variant="subtitle1" paddingBottom={1}>
                  {t(missing.section)}
                </Typography>
                <IconButton
                  sx={{ padding: 0, marginTop: -0.5 }}
                  onClick={() =>
                    navigate(
                      `/employees/${employee?.id + employeeRoutes[missing.section as EmployeeRoutes]}`
                    )
                  }
                >
                  {employee?.user && <LinkShearIcon />}
                </IconButton>
              </Box>
              <ul>
                {missing.fields?.map((field: string) => (
                  <li key={missing.section + field}>
                    <Typography variant="body2">{t(field)}</Typography>
                  </li>
                ))}
              </ul>
            </RoundedPaper>
          );
        })}
    </>
  );
}

export default HasMissingFieldsCell;
